// Description: This component displays a row of event information, including the event image, title, dates, composers, and venue. It also includes a "More" button for additional details.
import React from "react";
import { NavLink } from "react-router-dom";
import slugify from "../utils/slugify";
import { getEventImage } from "../pages/Event/_components/utils";
import { useTranslation } from "react-i18next";

function EventRow({ event, page, pageImage }) {
  const { t } = useTranslation();
  // Defensive check for invalid event
  if (!event || !event._id || !event.date_times) {
    return null;
  }

  const currentDate = new Date("2025-04-06");

  // Filter future dates
  const futureDates = event.date_times
    .map((dt) => new Date(dt.date))
    .filter((date) => date > currentDate);

  // Custom date formatting: "April 17, 24, 2025"
  const formatDates = (dates) => {
    if (!dates || dates.length === 0) return "";
    const groupedDates = {};
    dates.forEach((date) => {
      const monthYear = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      if (!groupedDates[monthYear]) groupedDates[monthYear] = [];
      groupedDates[monthYear].push(date.getDate());
    });
    return Object.entries(groupedDates)
      .map(([monthYear, days]) => {
        const sortedDays = days.sort((a, b) => a - b);
        return `${sortedDays.join(", ")} ${monthYear.split(" ")[0]} ${
          monthYear.split(" ")[1]
        }`;
      })
      .join(", ");
  };

  const formattedDates = formatDates(futureDates);

  // Get composers (already populated from API)
  const composerNames =
    event.composers?.map((composer) => composer?.name).filter((name) => name) ||
    [];

  const composerText =
    composerNames.length > 0
      ? composerNames.length > 1
        ? `${composerNames[0]} and others`
        : composerNames[0]
      : "";

  const famousPerformers = Object.values(event.cast || {})
    .flat()
    .filter((performer) => performer?.famousPerformer);

  const famousPerformerNames = famousPerformers.map((p) => p.name);

  // Opera house name for routing
  const operaHouseName = slugify(event.venue?.name) || "unknown";

  // Handle broken image links
  const handleImageError = (e) => {
    e.target.style.display = "none";
  };
  const imageUrl = getEventImage(event, page, pageImage);
  return (
    <NavLink to={`/${operaHouseName}/${event.eventSlug}`}>
      <div className="flex max-full min-h-[265px] bg-[#f1f1f1] border-b border-white my-4 p-3 md:flex-row flex-col hover:scale-[1.01] transition-transform">
        <div className=" bg-white md:max-w-[39%] w-full shadow-xl flex-shrink-0 bg-transparent">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={event.title || t("eventRow.eventImage")}
              className="w-full md:h-full h-auto object-cover aspect-7/4"
              onError={handleImageError}
            />
          )}
          {!imageUrl && (
            <div className="w-full h-full bg-white flex items-center justify-center text-black text-base rounded-lg"></div>
          )}
        </div>
        <div className="bg-white flex-1 md:max-w-[39%] md:pl-4 p-4 md:p-4 flex flex-col text-left md:w-[300px] w-ful mb-2 md:mr-2 md:mb-0 shadow-xl">
          <div className="flex justify-between items-start md:mb-2">
            <NavLink to={`/${operaHouseName}/${event.eventSlug}`}>
              <h2 className="text-2xl capitalize m-0 text-opera-red">
                {event.title || t("eventRow.untitledEvent")}
              </h2>
            </NavLink>
          </div>
          <div className="grid grid-cols-1 content-between h-full">
            <div>
              {event.tags && event.tags.length > 0 && (
                <p className="text-base font-normal text-black m-[0.2rem_0] leading-[1.5]">
                  {event.tags
                    .map((tag) => tag.charAt(0).toUpperCase() + tag.slice(1))
                    .join(", ")}
                </p>
              )}
              <p className="text-base  font-normal text-black m-[0.2rem_0] leading-[1.5]">
                {formattedDates}
              </p>
              <p className="text-base font-normal text-black m-[0.2rem_0] leading-[1.5]">
                {event.genre
                  ? event.genre.toLowerCase() === "dance"
                    ? "Ballet"
                    : event.genre.charAt(0).toUpperCase() + event.genre.slice(1)
                  : ""}{" "}
                {composerText ? `| ${composerText}` : ""}
              </p>
              {famousPerformerNames.length > 0 && (
                <p className="text-base font-normal text-black m-[0.2rem_0] leading-[1.5]">
                  {t("eventRow.with")}{" "}
                  {famousPerformerNames.length > 1
                    ? famousPerformerNames.slice(0, -1).join(", ") +
                      " & " +
                      famousPerformerNames.slice(-1)
                    : famousPerformerNames[0]}
                </p>
              )}
              <br />
            </div>
            <p className="text-base font-normal text-black m-[0.2rem_0] leading-[1.5] align-self-end">
              {event.venue?.name
                ? `${event.venue.name}, ${
                    event.venue?.cityNew?.name || t("eventRow.unknown")
                  }`
                : t("eventRow.unknownVenue")}
            </p>
          </div>
        </div>
        <div className="bg-white md:items-center md:w-[22%] p-4 flex flex-col justify-between gap-2 shadow-xl">
          <p className="text-black text-center text-lg font-normal">
            {t("eventRow.ticketsAccommodations")}
          </p>
          <div className="flex justify-center gap-1">
            <img
              src="/images/tickets.jpg"
              alt={t("eventRow.tickets")}
              className="w-7 h-7 object-cover rounded"
            />
            +
            <img
              src="/images/hotel.jpg"
              alt={t("eventRow.hotels")}
              className="w-7 h-7 object-cover rounded"
            />
            +
            <img
              src="/images/breakfast1.png"
              alt={t("eventRow.breakfast")}
              className="w-8 h-8 -mt-1 -ml-1 object-cover rounded"
            />
          </div>
          <NavLink
            to={`/${operaHouseName}/${event.eventSlug}`}
            className="px-4 py-2 bg-opera-red text-center text-white rounded text-base font-normal hover:bg-[#a60505] transition-colors"
          >
            {t("eventRow.checkAvailability")}
          </NavLink>
        </div>
      </div>
    </NavLink>
  );
}

export default EventRow;
